import { computed, inject, Injectable, signal } from '@angular/core';
import {
  GroupedNotifications,
  NotificationItem,
} from '@libs/core/notification/responses/notification.response';
import { NotificationSseEventPayload } from '@api-types/sse/event-payloads/notification.sse-event-payload';
import { NotificationHttpClient } from './notification-http-client';

@Injectable({
  providedIn: 'root',
})
export class NotificationStateService {
  private readonly httpClient = inject(NotificationHttpClient);

  private readonly _notifications = signal<GroupedNotifications>({
    today: [],
    lastWeek: [],
    older: [],
  });
  private readonly _unreadCount = signal<number>(0);
  private readonly _loading = signal<boolean>(false);
  private readonly _initialized = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _currentPage = signal<number>(0);
  private readonly _total = signal<number>(0);
  private readonly _hasMore = signal<boolean>(false);

  readonly error = this._error.asReadonly();

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();

  readonly hasUnread = computed(() => this._unreadCount() > 0);

  fetchNotifications(): void {
    if (this._loading()) {
      return;
    }

    this._loading.set(true);
    this._error.set(null);

    this.httpClient.getNotifications(1).subscribe({
      next: (response) => {
        this._notifications.set(response.notifications);
        this._unreadCount.set(response.unreadCount);
        this._currentPage.set(1);
        this._total.set(response.total);
        this._hasMore.set(1 * response.limit < response.total);
        this._loading.set(false);
        this._initialized.set(true);
      },
      error: (err) => {
        console.error('Failed to load notifications', err);
        this._loading.set(false);
        this._error.set(err?.error?.message || 'Failed to load notifications');
      },
    });
  }

  loadMore(): void {
    if (this._loading() || !this._hasMore()) {
      return;
    }

    const nextPage = this._currentPage() + 1;
    this._loading.set(true);
    this._error.set(null);

    this.httpClient.getNotifications(nextPage).subscribe({
      next: (response) => {
        this._notifications.update((prev) =>
          this.mergeGroupedNotifications(prev, response.notifications),
        );
        this._currentPage.set(nextPage);
        this._total.set(response.total);
        this._hasMore.set(nextPage * response.limit < response.total);
        this._loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load more notifications', err);
        this._loading.set(false);
        this._error.set(
          err?.error?.message || 'Failed to load more notifications',
        );
      },
    });
  }

  fetchUnreadCount(): void {
    this.httpClient.getUnreadCount().subscribe({
      next: (response) => {
        this._unreadCount.set(response.count);
        this._initialized.set(true);
      },
      error: (err) => {
        console.error('Failed to load unread count', err);
        this._error.set(err?.error?.message || 'Failed to load unread count');
      },
    });
  }

  markAsRead(notificationId: string): void {
    this.httpClient.markAsRead(notificationId).subscribe({
      next: () => {
        this.updateNotificationReadState(notificationId);
        this._unreadCount.update((count) => Math.max(0, count - 1));
      },
      error: (err) => {
        console.error('Failed to mark notification as read', err);
        this._error.set(
          err?.error?.message || 'Failed to mark notification as read',
        );
      },
    });
  }

  decrementUnreadCount(amount: number): void {
    this._unreadCount.update((count) => Math.max(0, count - amount));
  }

  addFromSse(payload: NotificationSseEventPayload): void {
    const item: NotificationItem = {
      id: payload.id,
      type: payload.type,
      actorName: payload.actorName,
      description: payload.description,
      referenceId: payload.referenceId,
      sourceId: payload.sourceId,
      navigationUrl: payload.navigationUrl,
      isRead: payload.isRead,
      createdAt: payload.createdAt,
    };

    this._notifications.update((groups) => ({
      ...groups,
      today: [item, ...groups.today],
    }));
    this._unreadCount.update((count) => count + 1);
    this._total.update((total) => total + 1);
  }

  initializeIfNeeded(): void {
    if (!this._initialized()) {
      this.fetchUnreadCount();
    }
  }

  private updateNotificationReadState(notificationId: string): void {
    const markRead = (items: NotificationItem[]): NotificationItem[] =>
      items.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n));

    this._notifications.update((groups) => ({
      today: markRead(groups.today),
      lastWeek: markRead(groups.lastWeek),
      older: markRead(groups.older),
    }));
  }

  private mergeGroupedNotifications(
    existing: GroupedNotifications,
    incoming: GroupedNotifications,
  ): GroupedNotifications {
    return {
      today: [...existing.today, ...incoming.today],
      lastWeek: [...existing.lastWeek, ...incoming.lastWeek],
      older: [...existing.older, ...incoming.older],
    };
  }
}
